/**
 * Mock Dashboard Data
 *
 * Example data simulating the processed JSON output.
 * This JSON represents the output of a reconciliation workflow.
 */

import type { DashboardConfig } from '../types/dashboard';
import { colorAt } from '../charts/theme/palette';

export const mockDashboardConfig: DashboardConfig = {
  id: 'dashboard-conciliacion-001',
  title: 'Reporte de Conciliación Minera',
  description: 'Análisis de discrepancias entre modelo de largo plazo (LP) y corto plazo (CP)',
  createdAt: '2025-01-22T10:00:00Z',

  layout: {
    columns: 2,
  },

  metadata: {
    author: 'Sistema de Conciliación',
    version: '1.0',
    periodo: 'Q4-2024',
  },

  charts: [
    {
      id: 'stacked-metal-ton-ley-periodo',
      type: 'stackedPanels',
      title: 'Metal, Tonelaje y Ley por Periodo',
      subtitle:
        '3 paneles apilados, cada uno con su propio color · eje X compartido · LP sólida con marcadores vs CP punteada',
      encoding: { xKey: 'periodo' },
      xAxisLabel: 'Año',
      series: [
        { dataKey: 'metalLP', name: 'Metal LP', panel: 0, color: colorAt(0), markers: true },
        { dataKey: 'metalCP', name: 'Metal CP', panel: 0, color: colorAt(0), dash: 'dash' },
        { dataKey: 'tonLP', name: 'Tonelaje LP', panel: 1, color: colorAt(1), markers: true },
        { dataKey: 'tonCP', name: 'Tonelaje CP', panel: 1, color: colorAt(1), dash: 'dash' },
        { dataKey: 'leyLP', name: 'Ley LP', panel: 2, color: colorAt(2), markers: true },
        { dataKey: 'leyCP', name: 'Ley CP', panel: 2, color: colorAt(2), dash: 'dash' },
      ],
      data: [
        {
          periodo: '2026',
          metalLP: 33000,
          metalCP: 27500,
          tonLP: 5.5,
          tonCP: 5.1,
          leyLP: 0.9,
          leyCP: 0.88,
        },
        {
          periodo: '2027',
          metalLP: 63000,
          metalCP: 55500,
          tonLP: 8.0,
          tonCP: 7.3,
          leyLP: 0.78,
          leyCP: 0.77,
        },
        {
          periodo: '2028',
          metalLP: 28200,
          metalCP: 28100,
          tonLP: 7.1,
          tonCP: 6.7,
          leyLP: 0.92,
          leyCP: 0.9,
        },
        {
          periodo: '2029',
          metalLP: 19500,
          metalCP: 17800,
          tonLP: 6.2,
          tonCP: 5.9,
          leyLP: 0.82,
          leyCP: 0.76,
        },
        {
          periodo: '2030',
          metalLP: 10000,
          metalCP: 7200,
          tonLP: 3.5,
          tonCP: 2.9,
          leyLP: 1.05,
          leyCP: 1.02,
        },
      ],
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
        colSpan: 2,
        cardHeight: 460,
        panels: [{ label: 'Metal (t)' }, { label: 'Tonelaje (Mt)' }, { label: 'Ley Cu (%)' }],
      },
    },
    {
      id: 'line-bar-metal-ley-periodo',
      type: 'lineBar',
      title: 'Metal y Ley por Periodo',
      subtitle: 'Metal fino en barras LP/CP (izq) · Ley en líneas LP/CP (der)',
      encoding: { xKey: 'periodo' },
      xAxisLabel: 'Año',
      yAxisLabel: 'Metal fino (t)',
      y2AxisLabel: 'Ley Cu (%)',
      series: [
        { dataKey: 'metalLP', name: 'Metal LP', type: 'bar', color: '#3B82F6' },
        { dataKey: 'metalCP', name: 'Metal CP', type: 'bar', color: '#F59E0B' },
        { dataKey: 'leyLP', name: 'Ley LP', type: 'line', color: '#3B82F6', yAxis: 'right' },
        { dataKey: 'leyCP', name: 'Ley CP', type: 'line', color: '#F59E0B', yAxis: 'right' },
      ],
      data: [
        { periodo: '2026', metalLP: 33000, metalCP: 27500, leyLP: 0.9, leyCP: 0.88 },
        { periodo: '2027', metalLP: 63000, metalCP: 55500, leyLP: 0.78, leyCP: 0.77 },
        { periodo: '2028', metalLP: 28200, metalCP: 28100, leyLP: 0.92, leyCP: 0.9 },
        { periodo: '2029', metalLP: 19500, metalCP: 17800, leyLP: 0.82, leyCP: 0.76 },
        { periodo: '2030', metalLP: 10000, metalCP: 7200, leyLP: 1.05, leyCP: 1.02 },
      ],
      options: { showLegend: true, showGrid: true, showTooltip: true },
    },
    {
      id: 'line-tonelaje-ley-dual',
      type: 'line',
      title: 'Tonelaje vs Ley por Periodo',
      subtitle: 'Doble eje Y: tonelaje (izq) · ley (der, punteada)',
      encoding: { xKey: 'periodo' },
      xAxisLabel: 'Periodo',
      yAxisLabel: 'Tonelaje (kt)',
      y2AxisLabel: 'Ley Cu (%)',
      series: [
        { dataKey: 'tonelaje', name: 'Tonelaje', color: '#3B82F6' },
        { dataKey: 'ley', name: 'Ley Cu', color: '#F59E0B', yAxis: 'right', dash: 'dot' },
      ],
      data: [
        { periodo: 'P1', tonelaje: 1200, ley: 0.82 },
        { periodo: 'P2', tonelaje: 1450, ley: 0.74 },
        { periodo: 'P3', tonelaje: 1310, ley: 0.91 },
        { periodo: 'P4', tonelaje: 1580, ley: 0.68 },
        { periodo: 'P5', tonelaje: 1420, ley: 0.79 },
      ],
      options: { showLegend: true, showGrid: true, showTooltip: true },
    },
    {
      id: 'bar-comparacion-backflag',
      type: 'bar',
      title: 'Comparación BD2025 vs Modelo MP2024',
      subtitle: 'Backflag ind_tipo_2025=1, datos válidos, N=6,919',
      encoding: { xKey: 'categoria' },
      xAxisLabel: 'Tipo',
      yAxisLabel: 'N registros',
      series: [
        { dataKey: 'bd', name: 'BD2025', color: '#3B82F6' },
        { dataKey: 'modelo', name: 'Modelo MP24', color: '#F59E0B' },
      ],
      data: [
        { categoria: 'PCO', bd: 1271, modelo: 1341 },
        { categoria: 'PRO', bd: 821, modelo: 825 },
        { categoria: 'TOB', bd: 1743, modelo: 1554 },
        { categoria: 'AND', bd: 1984, modelo: 2308 },
        { categoria: 'USED', bd: 917, modelo: 694 },
      ],
      statistics: [
        { label: 'N', value: '6,919' },
        { label: 'Δ medio', value: '+7%' },
      ],
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
        showDataLabels: true,
        showStatsBox: true,
      },
    },
    {
      id: 'line-error-acumulado',
      type: 'line',
      title: 'Error Acumulado por Periodo',
      subtitle: 'Tonelaje, Ley y Metal',
      encoding: { xKey: 'periodo' },
      xAxisLabel: 'Periodo',
      yAxisLabel: 'Error Acumulado (%)',
      series: [
        { dataKey: 'errorTonelaje', name: 'Tonelaje', color: '#3B82F6' },
        { dataKey: 'errorLey', name: 'Ley', color: '#10B981' },
        { dataKey: 'errorMetal', name: 'Metal', color: '#F59E0B' },
      ],
      datasetId: 'dataset-line-error-acumulado',
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
        showYZeroLine: true,
      },
    },
    {
      id: 'scatter-acierto-ley-corte',
      type: 'scatter',
      title: 'Acierto según Ley de Corte',
      subtitle: 'Comparación LP vs CP',
      encoding: { xKey: 'leyLP', yKey: 'leyCP' },
      xAxisLabel: 'Ley LP (%)',
      yAxisLabel: 'Ley CP (%)',
      datasetId: 'dataset-scatter-acierto-ley-corte',
      options: {
        showLegend: false,
        showGrid: true,
        showTooltip: true,
        showReferenceLine: true,
      },
    },
    {
      id: 'line-error-periodo',
      type: 'line',
      title: 'Error por Periodo',
      subtitle: 'Tonelaje, Ley y Metal',
      encoding: { xKey: 'periodo' },
      xAxisLabel: 'Periodo',
      yAxisLabel: 'Error (%)',
      series: [
        { dataKey: 'errorTonelaje', name: 'Tonelaje', color: '#3B82F6' },
        { dataKey: 'errorLey', name: 'Ley', color: '#10B981' },
        { dataKey: 'errorMetal', name: 'Metal', color: '#F59E0B' },
      ],
      datasetId: 'dataset-line-error-periodo',
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
        showYZeroLine: true,
      },
    },
    {
      id: 'line-comparacion-periodo',
      type: 'line',
      title: 'Comparación LP vs CP',
      subtitle: 'Tonelaje por periodo',
      encoding: { xKey: 'periodo' },
      xAxisLabel: 'Periodo',
      yAxisLabel: 'Tonelaje (kt)',
      series: [
        { dataKey: 'tonelajeLP', name: 'LP', color: '#3B82F6' },
        { dataKey: 'tonelajeCP', name: 'CP', color: '#10B981' },
      ],
      datasetId: 'dataset-line-comparacion-periodo',
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
      },
    },
    {
      id: 'line-acierto-destino',
      type: 'line',
      title: 'Acierto en Destino',
      subtitle: 'Según rango de ley de corte',
      encoding: { xKey: 'rangoLey' },
      xAxisLabel: 'Rango Ley de Corte (%)',
      yAxisLabel: 'Acierto (%)',
      series: [{ dataKey: 'acierto', name: 'Acierto', color: '#10B981' }],
      datasetId: 'dataset-line-acierto-destino',
      options: {
        showLegend: false,
        showGrid: true,
        showTooltip: true,
      },
    },
    {
      id: 'spatial-acierto-destino',
      type: 'spatialCategorical',
      title: 'Mapa de Acierto en Destino',
      subtitle: 'Clasificación Ore-Waste por zona y ley de corte',
      encoding: { xKey: 'x', yKey: 'y', groupKey: 'clasificacion' },
      xAxisLabel: 'Coordenada X',
      yAxisLabel: 'Coordenada Y',
      groupColors: {
        'ORO -> ORO(ACIERTO)': '#10B981',
        'WASTE -> WASTE(ACIERTO)': '#3B82F6',
        'ORO -> WASTE(ERROR)': '#EF4444',
        'WASTE -> ORO(ERROR)': '#8B5CF6',
      },
      datasetId: 'dataset-spatial-acierto-destino',
      options: {
        showLegend: true,
        showTooltip: true,
      },
    },
    {
      id: 'spatial-acierto-geologia',
      type: 'spatialCategorical',
      title: 'Mapa de Acierto en Geología',
      subtitle: 'Clasificación geológica por ubicación espacial',
      encoding: { xKey: 'x', yKey: 'y', groupKey: 'resultado' },
      xAxisLabel: 'Coordenada X',
      yAxisLabel: 'Coordenada Y',
      groupColors: {
        'Pórfido-Acierto': '#3B82F6',
        'Brecha-Acierto': '#10B981',
        'Skarn-Acierto': '#F59E0B',
        'Hornfels-Acierto': '#8B5CF6',
        Error: '#EF4444',
      },
      data: [
        ...Array.from({ length: 80 }, () => {
          const x = 23.5 + Math.random() * 2;
          const y = 26 + Math.random() * 2;
          const rand = Math.random();
          let resultado: string;
          if (rand < 0.3) resultado = 'Pórfido-Acierto';
          else if (rand < 0.5) resultado = 'Brecha-Acierto';
          else if (rand < 0.65) resultado = 'Skarn-Acierto';
          else if (rand < 0.8) resultado = 'Hornfels-Acierto';
          else resultado = 'Error';
          return {
            x: parseFloat(x.toFixed(2)),
            y: parseFloat(y.toFixed(2)),
            resultado,
          };
        }),
      ],
      options: {
        showLegend: true,
        showTooltip: true,
      },
    },
    {
      id: 'line-acierto-geologia',
      type: 'line',
      title: 'Acierto en Geología',
      subtitle: 'Por tipo de roca',
      encoding: { xKey: 'tipoRoca' },
      xAxisLabel: 'Tipo de Roca',
      yAxisLabel: 'Acierto (%)',
      series: [{ dataKey: 'acierto', name: 'Acierto', color: '#8B5CF6' }],
      data: [
        { tipoRoca: 'Pórfido', acierto: 88 },
        { tipoRoca: 'Brecha', acierto: 71 },
        { tipoRoca: 'Skarn', acierto: 83 },
        { tipoRoca: 'Hornfels', acierto: 62 },
        { tipoRoca: 'Otro', acierto: 75 },
      ],
      options: {
        showLegend: false,
        showGrid: true,
        showTooltip: true,
      },
    },
    {
      id: 'line-curva-tonelaje-ley',
      type: 'line',
      title: 'Curva Tonelaje-Ley',
      subtitle: 'Comparación LP vs CP',
      encoding: { xKey: 'leyCorte' },
      xAxisLabel: 'Ley de Corte (%)',
      yAxisLabel: 'Tonelaje (Mt)',
      series: [
        { dataKey: 'tonelajeLP', name: 'LP', color: '#3B82F6' },
        { dataKey: 'tonelajeCP', name: 'CP', color: '#10B981' },
      ],
      data: Array.from({ length: 21 }, (_, i) => {
        const leyCorte = i * 0.1;
        return {
          leyCorte: parseFloat(leyCorte.toFixed(1)),
          tonelajeLP: parseFloat((50 * Math.exp(-0.8 * leyCorte)).toFixed(1)),
          tonelajeCP: parseFloat(
            (48 * Math.exp(-0.75 * leyCorte) + (Math.random() - 0.5) * 2).toFixed(1)
          ),
        };
      }),
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
      },
    },
    {
      id: 'line-limits-example',
      type: 'line',
      title: 'Ejemplo Límites Superior/Inferior',
      subtitle: 'Líneas horizontales de límite (upper/lower)',
      encoding: { xKey: 'periodo' },
      xAxisLabel: 'Periodo',
      yAxisLabel: 'Valor',
      series: [{ dataKey: 'valor', name: 'Valor Medido', color: '#3B82F6' }],
      data: [
        { periodo: 'P1', valor: 4 },
        { periodo: 'P2', valor: 6 },
        { periodo: 'P3', valor: 7 },
        { periodo: 'P4', valor: 9 },
        { periodo: 'P5', valor: 5 },
      ],
      limits: {
        upper: 8,
        lower: 3,
        upperLabel: 'Límite Superior',
        lowerLabel: 'Límite Inferior',
        upperColor: '#EF4444',
        lowerColor: '#10B981',
      },
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
      },
    },
    {
      id: 'distribution-dh-bh',
      type: 'distribution',
      title: 'Distribución DH vs BH',
      subtitle: 'Comparación de leyes en modelo',
      encoding: { xKey: 'bin' },
      xAxisLabel: 'Ley Cu (%)',
      yAxisLabel: 'Frecuencia',
      series: [
        { dataKey: 'frecuenciaDH', name: 'DH (Drill Holes)', color: '#3B82F6' },
        { dataKey: 'frecuenciaBH', name: 'BH (Blast Holes)', color: '#F59E0B' },
      ],
      data: [
        { bin: '0.0-0.2', frecuenciaDH: 12, frecuenciaBH: 18 },
        { bin: '0.2-0.4', frecuenciaDH: 28, frecuenciaBH: 35 },
        { bin: '0.4-0.6', frecuenciaDH: 52, frecuenciaBH: 48 },
        { bin: '0.6-0.8', frecuenciaDH: 78, frecuenciaBH: 72 },
        { bin: '0.8-1.0', frecuenciaDH: 95, frecuenciaBH: 88 },
        { bin: '1.0-1.2', frecuenciaDH: 82, frecuenciaBH: 78 },
        { bin: '1.2-1.4', frecuenciaDH: 58, frecuenciaBH: 62 },
        { bin: '1.4-1.6', frecuenciaDH: 35, frecuenciaBH: 42 },
        { bin: '1.6-1.8', frecuenciaDH: 18, frecuenciaBH: 22 },
        { bin: '1.8-2.0', frecuenciaDH: 8, frecuenciaBH: 12 },
        { bin: '>2.0', frecuenciaDH: 5, frecuenciaBH: 8 },
      ],
      options: {
        showLegend: true,
        showGrid: true,
        showTooltip: true,
        overlapped: true,
      },
    },
    {
      id: 'bar-stacked-produccion',
      type: 'bar',
      title: 'Producción Apilada por Periodo',
      subtitle: 'Barras apiladas (options.stacked)',
      encoding: { xKey: 'periodo' },
      xAxisLabel: 'Periodo',
      yAxisLabel: 'Tonelaje (kt)',
      series: [
        { dataKey: 'oxido', name: 'Óxido', color: '#3B82F6' },
        { dataKey: 'sulfuro', name: 'Sulfuro', color: '#F59E0B' },
        { dataKey: 'mixto', name: 'Mixto', color: '#10B981' },
      ],
      data: [
        { periodo: 'P1', oxido: 120, sulfuro: 80, mixto: 30 },
        { periodo: 'P2', oxido: 140, sulfuro: 95, mixto: 25 },
        { periodo: 'P3', oxido: 110, sulfuro: 105, mixto: 40 },
        { periodo: 'P4', oxido: 130, sulfuro: 90, mixto: 35 },
      ],
      options: { showLegend: true, showGrid: true, showTooltip: true, stacked: true },
    },
    {
      id: 'bar-error-bars-recuperacion',
      type: 'bar',
      title: 'Recuperación con Barras de Error',
      subtitle: 'Error simétrico vía series[].errorKey',
      encoding: { xKey: 'tipoRoca' },
      xAxisLabel: 'Tipo de Roca',
      yAxisLabel: 'Recuperación (%)',
      series: [{ dataKey: 'recuperacion', name: 'Recuperación', color: '#3B82F6', errorKey: 'sd' }],
      data: [
        { tipoRoca: 'Pórfido', recuperacion: 88, sd: 3 },
        { tipoRoca: 'Brecha', recuperacion: 71, sd: 5 },
        { tipoRoca: 'Skarn', recuperacion: 83, sd: 4 },
        { tipoRoca: 'Hornfels', recuperacion: 62, sd: 6 },
      ],
      options: { showLegend: false, showGrid: true, showTooltip: true },
    },
    {
      id: 'box-ley-por-zona',
      type: 'box',
      title: 'Distribución de Ley por Zona',
      subtitle: 'Box plot agrupado (encoding.groupKey)',
      encoding: { yKey: 'ley', groupKey: 'zona' },
      yAxisLabel: 'Ley Cu (%)',
      groupColors: { Norte: '#3B82F6', Centro: '#10B981', Sur: '#F59E0B' },
      data: Array.from({ length: 90 }, (_, i) => {
        const zona = ['Norte', 'Centro', 'Sur'][i % 3];
        const base = zona === 'Norte' ? 0.9 : zona === 'Centro' ? 1.3 : 0.6;
        return { zona, ley: parseFloat((base + Math.random() * 0.8).toFixed(2)) };
      }),
      options: { showLegend: true, showTooltip: true },
    },
    {
      id: 'histogram2d-lp-cp',
      type: 'histogram2d',
      title: 'Densidad LP vs CP',
      subtitle: 'Histograma 2D (encoding.xKey + yKey numéricos)',
      encoding: { xKey: 'leyLP', yKey: 'leyCP' },
      xAxisLabel: 'Ley LP (%)',
      yAxisLabel: 'Ley CP (%)',
      data: Array.from({ length: 400 }, () => {
        const leyLP = parseFloat((Math.random() * 2).toFixed(2));
        const leyCP = parseFloat(Math.max(0, leyLP + (Math.random() - 0.5) * 0.6).toFixed(2));
        return { leyLP, leyCP };
      }),
      options: { showTooltip: true },
    },
    {
      id: 'pie-tonelaje-por-tipo',
      type: 'pie',
      title: 'Tonelaje por Tipo de Mineral',
      subtitle: 'Pie (groupKey = etiquetas, yKey = valores)',
      encoding: { groupKey: 'tipo', yKey: 'tonelaje' },
      groupColors: { Óxido: '#3B82F6', Sulfuro: '#F59E0B', Mixto: '#10B981' },
      data: [
        { tipo: 'Óxido', tonelaje: 500 },
        { tipo: 'Sulfuro', tonelaje: 385 },
        { tipo: 'Mixto', tonelaje: 130 },
      ],
      options: { showLegend: true, showTooltip: true },
    },
  ],
};
